import type { NavigateFunction } from 'react-router-dom';
import type { TFixedPosition } from '@kognitos/lattice';

/**
 * Unique identifier for each nudge in the application.
 * Add new nudge IDs here as new nudges are created.
 */
export enum ENudgeId {
  CREATE_FIRST_DRAFT = 'create_first_draft',
}

/**
 * Analytics configuration for a nudge.
 */
export interface INudgeAnalyticsConfig {
  /** Whether analytics tracking is enabled. Defaults to true. */
  enabled?: boolean;
  /** Prefix for analytics events. Defaults to `nudge_{nudgeId}`. */
  eventPrefix?: string;
  /** Additional metadata to include with every analytics event. */
  metadata?: Record<string, string>;
}

/**
 * Persistence configuration for remembering nudge dismissal state.
 */
export interface INudgePersistenceConfig {
  /** Storage strategy. Defaults to 'localStorage'. */
  strategy?: 'localStorage' | 'none';
  /** Custom localStorage key. Defaults to `nudge_dismissed_{nudgeId}`. */
  key?: string;
}

/**
 * Static configuration for a single nudge.
 *
 * Nudges are sticky, non-blocking prompts that appear when a user
 * hasn't completed a key action. They persist across page navigations
 * until dismissed or the primary action is taken.
 */
export interface INudgeConfig {
  /** Unique nudge identifier. */
  id: ENudgeId;
  /** i18n key for the nudge title. */
  titleKey: string;
  /** i18n key for the nudge description. */
  descriptionKey: string;
  /** i18n key for the primary action button label. */
  actionLabelKey: string;
  /** i18n key for the dismiss button label. Defaults to 'common.action.dismiss'. */
  dismissLabelKey?: string;
  /**
   * Callback invoked when the primary action button is clicked.
   * Receives context helpers (`dismiss`, `navigate`, `orgId`, `workspaceId`)
   * so the handler can perform any combination of actions.
   * If not provided, clicking the action button only dismisses the nudge.
   */
  onAction?: (ctx: INudgeActionContext) => void;
  /** i18n namespace for resolving keys. Defaults to 'tours'. */
  namespace?: string;
  /**
   * Priority for conflict resolution when multiple nudges are active.
   * Higher values are shown first.
   */
  priority: number;
  /**
   * Viewport anchor position for the nudge card.
   * @default 'bottom-left'
   */
  position?: TFixedPosition;
  /**
   * Current step number in the onboarding sequence (for "N / M" display).
   * Omit to hide the step indicator.
   */
  stepNumber?: number;
  /** Total number of onboarding steps (for "N / M" display). */
  totalSteps?: number;
  /** Analytics configuration. */
  analytics?: INudgeAnalyticsConfig;
  /** Persistence configuration. */
  persistence?: INudgePersistenceConfig;
  /**
   * Optional Statsig feature gate name for per-nudge gating.
   * NOTE: Not yet evaluated — reserved for future use.
   */
  featureGate?: string;
}

/**
 * Context passed to a nudge's `onAction` callback.
 */
export interface INudgeActionContext {
  /** Marks the nudge as dismissed in storage. */
  dismiss: () => void;
  /** React Router navigate function for SPA navigation. */
  navigate: NavigateFunction;
  /** Active organization ID. */
  orgId: string;
  /** Active workspace ID. */
  workspaceId: string;
}

/**
 * A nudge config paired with its runtime condition, used by the NudgeManager.
 */
export interface INudgeEntry {
  /** Static nudge configuration. */
  config: INudgeConfig;
  /** Whether this nudge's condition is currently met (should be shown). */
  isActive: boolean;
}
